import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import { toast } from 'react-hot-toast';
import Header from 'shared/components/Header';
import { useAuth } from 'shared/hooks/useAuth';
import useProfilePhotoSrc from 'shared/hooks/useProfilePhotoSrc';
import useGroupService from 'shared/api/services/groupService';
import {
  getReportFormPalette as getFormPalette,
  REPORT_PRINT_KEEP_CSS,
  reportHeaderPrintSx,
} from 'shared/utils/reportFormPalette';
import { downloadFormPdf } from 'shared/utils/downloadFormPdf';

const HEADER_BLUE = '#0D2240';
const FORM_PRINT_ROOT_ID = 'users-permissions-report';
const PRINTING_BODY_CLASS = 'sr-printing-users-permissions-report';

function dash(value) {
  const v = typeof value === 'string' ? value.trim() : value;
  return v || v === 0 ? String(v) : '—';
}

function formatDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function UsersPermissionsReportPage() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { user } = useAuth();
  const { fetchUsersPermissionsReport } = useGroupService();

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        const data = await fetchUsersPermissionsReport();
        if (!cancelled) setReport(data);
      } catch (error) {
        if (!cancelled) {
          setReport(null);
          setLoadError(
            error.response?.data?.message || error.message || 'Failed to load users permissions report.'
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchUsersPermissionsReport]);

  const profile = user?.data;
  const tenantBranding = profile?.tenantBranding;
  const form = useMemo(
    () => getFormPalette(isDark, tenantBranding),
    [isDark, tenantBranding?.backgroundColor, tenantBranding?.primaryColor, tenantBranding?.secondaryColor]
  );
  const tenantLogoSrc = useProfilePhotoSrc(tenantBranding?.tenantPhoto);
  const tenantDisplayName =
    (profile?.tenantName && String(profile.tenantName).trim())
    || (tenantBranding?.name && String(tenantBranding.name).trim())
    || 'Organisation';

  const groups = report?.groups ?? [];

  const handlePrint = useCallback(() => {
    const previousTitle = document.title;
    document.title = ' ';
    const cleanup = () => {
      document.title = previousTitle;
      document.body.classList.remove(PRINTING_BODY_CLASS);
      window.removeEventListener('afterprint', cleanup);
    };
    document.body.classList.add(PRINTING_BODY_CLASS);
    window.addEventListener('afterprint', cleanup);
    window.print();
    setTimeout(cleanup, 1500);
  }, []);

  const handleDownloadPdf = useCallback(async () => {
    if (downloadingPdf || !report) return;
    const formEl = document.getElementById(FORM_PRINT_ROOT_ID);
    if (!formEl) return;
    setDownloadingPdf(true);
    try {
      const stamp = new Date().toISOString().slice(0, 10);
      await downloadFormPdf({
        element: formEl,
        fileName: `users-permissions-report-${stamp}.pdf`,
      });
      toast.success('PDF downloaded.');
    } catch (error) {
      console.error(error);
      toast.error('Failed to download PDF. Please try again.');
    } finally {
      setDownloadingPdf(false);
    }
  }, [downloadingPdf, report]);

  useEffect(() => {
    const style = document.createElement('style');
    style.setAttribute('data-users-permissions-report-print', 'true');
    style.textContent = `
      @page { size: A4; margin: 0; }
      @media print {
        html, body {
          margin: 0 !important;
          padding: 0 !important;
          background: #fff !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        body.${PRINTING_BODY_CLASS} .no-print { display: none !important; }
        ${REPORT_PRINT_KEEP_CSS}
      }
    `;
    document.head.appendChild(style);
    return () => style.remove();
  }, []);

  if (loading) {
    return (
      <Box m={{ xs: '8px', sm: '12px' }} display="flex" justifyContent="center" py={8}>
        <CircularProgress size={36} />
      </Box>
    );
  }

  if (!report) {
    return (
      <Box m={{ xs: '8px', sm: '12px' }}>
        <Header title="Users Permissions Report" />
        <Paper variant="outlined" sx={{ p: 3, mt: 2, borderRadius: 2 }}>
          <Typography variant="body1">
            {loadError || 'Unable to load the users permissions report.'}
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      m={{ xs: '8px', sm: '12px' }}
      sx={{
        height: 'calc(100vh - 96px)',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        '@media print': {
          height: 'auto',
          m: 0,
          bgcolor: '#fff',
          '& .no-print': { display: 'none !important' },
        },
      }}
    >
      <Box className="no-print">
        <Header
          title="Users Permissions Report"
          subtitle="Tenant audit of groups, permissions, and assigned users"
        />
      </Box>

      <Box
        className="no-print"
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
        mb={2}
      >
        <Button type="button" startIcon={<PrintOutlinedIcon />} onClick={handlePrint} variant="outlined">
          Print
        </Button>
        <Button
          type="button"
          startIcon={
            downloadingPdf ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdfOutlinedIcon />
          }
          onClick={handleDownloadPdf}
          variant="contained"
          color="secondary"
          disabled={downloadingPdf}
        >
          {downloadingPdf ? 'Preparing PDF…' : 'Download PDF'}
        </Button>
      </Box>

      <Paper
        id={FORM_PRINT_ROOT_ID}
        elevation={0}
        sx={{
          overflow: 'auto',
          bgcolor: form.surface,
          color: form.text,
          borderRadius: 0,
          border: form.border,
          maxWidth: 980,
          mx: 'auto',
          width: '100%',
          '@media print': {
            border: 'none',
            maxWidth: '100%',
            overflow: 'visible',
            bgcolor: '#fff !important',
            color: '#111 !important',
            boxShadow: 'none',
          },
        }}
      >
        <Box
          sx={{
            bgcolor: form.banner,
            color: form.bannerText,
            px: { xs: 1.5, sm: 2.5 },
            py: 1.75,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
            '@media print': reportHeaderPrintSx,
          }}
        >
          <Box
            sx={{
              width: { xs: 108, sm: 148 },
              height: { xs: 72, sm: 96 },
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: form.logoBg,
              overflow: 'hidden',
            }}
          >
            {tenantLogoSrc ? (
              <Box
                component="img"
                src={tenantLogoSrc}
                alt={`${tenantDisplayName} logo`}
                sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
              />
            ) : (
              <Typography sx={{ color: HEADER_BLUE, fontWeight: 800, fontSize: '0.7rem', textAlign: 'center' }}>
                LOGO
              </Typography>
            )}
          </Box>
          <Box flex={1} minWidth={140}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.15rem', sm: '1.5rem' },
                letterSpacing: 0.2,
                lineHeight: 1.25,
                color: form.bannerText,
              }}
            >
              {tenantDisplayName}
            </Typography>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.05rem', sm: '1.25rem' },
                mt: 0.4,
                color: form.bannerText,
              }}
            >
              Users Permissions Report
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: form.bannerText, opacity: 0.85, mt: 0.4 }}>
              Generated {formatDateTime(report.generatedAt)} · {report.groupCount ?? groups.length} group
              {(report.groupCount ?? groups.length) === 1 ? '' : 's'}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ height: 4, bgcolor: form.accent }} />

        <Box px={{ xs: 1.5, sm: 2.5 }} py={2}>
          {groups.length === 0 ? (
            <Typography sx={{ color: form.muted }}>No groups found for this tenant.</Typography>
          ) : (
            groups.map((group) => {
              const permissions = group.permissions ?? [];
              const members = group.users ?? [];
              return (
                <Box
                  key={group.id || group.name}
                  mb={3}
                  sx={{
                    border: form.border,
                    pageBreakInside: 'avoid',
                  }}
                >
                  <Box
                    sx={{
                      px: 1.5,
                      py: 1,
                      bgcolor: form.tableHead,
                      borderBottom: form.border,
                    }}
                  >
                    <Typography sx={{ fontWeight: 800, fontSize: '1rem' }}>
                      {dash(group.name)}
                    </Typography>
                  </Box>

                  <Box
                    display="grid"
                    gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
                    sx={{ '& > *': { minWidth: 0 } }}
                  >
                    <Box sx={{ borderRight: { md: form.border }, borderBottom: { xs: form.border, md: 'none' } }}>
                      <Typography sx={{ px: 1.5, pt: 1.25, pb: 0.5, fontWeight: 700, fontSize: '0.82rem' }}>
                        Permissions ({permissions.length})
                      </Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ color: 'inherit', fontWeight: 700, borderColor: 'inherit' }}>
                              Permission
                            </TableCell>
                            <TableCell sx={{ color: 'inherit', fontWeight: 700, borderColor: 'inherit' }}>
                              Description
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {permissions.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={2} sx={{ color: form.muted, borderColor: 'inherit' }}>
                                No permissions assigned
                              </TableCell>
                            </TableRow>
                          ) : (
                            permissions.map((permission) => (
                              <TableRow key={permission.id || permission.permission}>
                                <TableCell sx={{ color: 'inherit', borderColor: 'inherit', fontSize: '0.8rem' }}>
                                  {dash(permission.permission || permission.name)}
                                </TableCell>
                                <TableCell sx={{ color: 'inherit', borderColor: 'inherit', fontSize: '0.8rem' }}>
                                  {dash(permission.description || permission.name)}
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </Box>
                    <Box>
                      <Typography sx={{ px: 1.5, pt: 1.25, pb: 0.5, fontWeight: 700, fontSize: '0.82rem' }}>
                        Users ({members.length})
                      </Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ color: 'inherit', fontWeight: 700, borderColor: 'inherit' }}>
                              Name
                            </TableCell>
                            <TableCell sx={{ color: 'inherit', fontWeight: 700, borderColor: 'inherit' }}>
                              Email
                            </TableCell>
                            <TableCell sx={{ color: 'inherit', fontWeight: 700, borderColor: 'inherit' }}>
                              Status
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {members.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={3} sx={{ color: form.muted, borderColor: 'inherit' }}>
                                No users assigned
                              </TableCell>
                            </TableRow>
                          ) : (
                            members.map((member) => (
                              <TableRow key={member.userId || member.email}>
                                <TableCell sx={{ color: 'inherit', borderColor: 'inherit', fontSize: '0.8rem' }}>
                                  {dash(member.name)}
                                </TableCell>
                                <TableCell sx={{ color: 'inherit', borderColor: 'inherit', fontSize: '0.8rem' }}>
                                  {dash(member.email)}
                                </TableCell>
                                <TableCell sx={{ color: 'inherit', borderColor: 'inherit', fontSize: '0.8rem' }}>
                                  {dash(member.status)}
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </Box>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
      </Paper>
    </Box>
  );
}
